import React from "react";

export interface NavMenuItemNode {
  id: string;
  label: React.ReactChild;
  depth: number;
  defaultActive?: boolean;
  childNodes?: NavMenuItemNode[];
}

/**
 * All Nav Menu Item components must support at least these props.
 */
export type NavMenuItemElementProps = {
  id: string;
  disabled?: boolean;
  defaultActive?: boolean;
  children: React.ReactNode | React.ReactNode[];
};

/**
 * @todo Replace this with an interface that is also extended by ALL Nav Menu Item components.
 */
export type NavMenuItemElement = React.ReactElement<NavMenuItemElementProps>;

export class NavMenuItemEmptyError extends Error {
  constructor(id: string, ...params: any) {
    super(...params);

    /**
     * Maintains proper stack trace for where our error was thrown (only available on V8)
     *
     * @see {@link https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Error#es6_custom_error_class}
     */
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, NavMenuItemEmptyError);
    }

    this.message = `Found a nav menu item with id ${id} that was empty! Expected at least one child element!`;
    this.name = this.constructor.name;

    /**
     * @see {@link https://github.com/facebook/jest/issues/8279#issuecomment-539775425}
     */
    Object.setPrototypeOf(this, NavMenuItemEmptyError.prototype);
  }
}

export class NavMenuItemMissingIdError extends Error {
  constructor(...params: any) {
    super(...params);

    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, NavMenuItemMissingIdError);
    }

    this.message = `Found a nav menu item with missing id!`;
    this.name = this.constructor.name;

    Object.setPrototypeOf(this, NavMenuItemMissingIdError.prototype);
  }
}

export class NavMenuItemDuplicateIdError extends Error {
  constructor(id: string, ...params: any) {
    super(...params);

    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, NavMenuItemDuplicateIdError);
    }

    this.message = `Found a nav menu item has duplicate id ${id}!`;
    this.name = this.constructor.name;

    Object.setPrototypeOf(this, NavMenuItemDuplicateIdError.prototype);
  }
}

export class NavMenuItemMultipleDefaultActiveError extends Error {
  constructor(...params: any) {
    super(...params);

    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, NavMenuItemMultipleDefaultActiveError);
    }

    this.message = "Found multiple nav menu items with defaultActive set to true!";
    this.name = this.constructor.name;

    Object.setPrototypeOf(this, NavMenuItemMultipleDefaultActiveError.prototype);
  }
}

/**
 * This is a utility function that converts a React node tree, comprising of a nav menu root element and its
 * arbitrarily nested nav menu items, in to a custom node tree.
 *
 * This can be useful for subsequent traversal and operations on the resulting tree for nav component logic.
 *
 * It also performs the following validations, and throws errors on failure:
 * - Checks for nav menu items are empty, as at least one child element (label) is required
 * - Checks for nodes with duplicate id within the tree
 * - Checks for multiple nodes with `defaultActive` set to `true`
 *
 * @param id Unique id of the current nav menu element
 * @param label The label of the current nav menu element
 * @param children React children of the current nav menu element
 * @param depth The depth of the current nav menu element
 * @param defaultActive Indicator if current nav menu element is active by default, only one such node allowed in a tree
 * @param defaultActiveCount Internal counter to check for multiple defaultActive
 * @param navMenuItemIds Internal set to identify duplicate ids
 * @returns A tree representation of the the nav menu and arbitrarily nested nav menu items
 */
export const makeNavMenuItemTree = (
  id: string,
  label: React.ReactChild,
  children: React.ReactChild | React.ReactChild[] | NavMenuItemElement[],
  depth: number = 0,
  defaultActive: boolean = false,
  defaultActiveCount: number = 0,
  navMenuItemIds: Set<string> = new Set()
): [NavMenuItemNode, number] => {
  if (!id) {
    throw new NavMenuItemMissingIdError();
  }

  let updatedDefaultActiveCount = defaultActiveCount;
  if (defaultActive) {
    updatedDefaultActiveCount++;
  }

  const menuItemNode: NavMenuItemNode = { id, label, depth, defaultActive };

  let childrenArray: NavMenuItemElement[];
  if (Array.isArray(children)) {
    childrenArray = children as NavMenuItemElement[];
  } else {
    childrenArray = React.Children.toArray(children) as NavMenuItemElement[];
  }

  if (childrenArray.length > 0) {
    let childNodes: NavMenuItemNode[] = [];
    let childNodesDefaultActiveCount = 0;

    for (const child of childrenArray) {
      const {
        id: childId,
        defaultActive: childDefaultActive,
        disabled: childDisabled,
        children: grandChildren,
      } = child.props;

      if (!childId) {
        throw new NavMenuItemMissingIdError();
      }

      const grandChildrenArray = React.Children.toArray(grandChildren) as React.ReactChild[];

      if (grandChildrenArray.length === 0) {
        throw new NavMenuItemEmptyError(childId);
      }

      if (navMenuItemIds.has(childId)) {
        throw new NavMenuItemDuplicateIdError(childId);
      } else {
        navMenuItemIds.add(childId);
      }

      const childLabel = grandChildrenArray[0];

      const prunedGrandChildren = grandChildrenArray.slice(1) as NavMenuItemElement[];

      const [childNode, childNodeDefaultActiveCount] = makeNavMenuItemTree(
        childId,
        childLabel,
        prunedGrandChildren,
        depth + 1,
        childDisabled ? false : childDefaultActive,
        0,
        navMenuItemIds
      );

      childNodes.push(childNode);
      childNodesDefaultActiveCount += childNodeDefaultActiveCount;
    }

    menuItemNode.childNodes = childNodes;
    updatedDefaultActiveCount += childNodesDefaultActiveCount;
  }

  if (updatedDefaultActiveCount > 1) {
    throw new NavMenuItemMultipleDefaultActiveError();
  }

  return [menuItemNode, updatedDefaultActiveCount];
};

/**
 * A utility function that traverses a tree of `NavMenuItemNode` nodes representing arbitrarily nested nav menu items,
 * and returns the path from the root to the currently active node. It uses the passed in `id` as the currently active
 * node.
 *
 * @param navMenuRoot The root of a Nav Menu represented as a tree of `NavMenuItemNode`s.
 * @param id `id` of the active nav mnu item.
 * @returns An array of `id` representing the path from root to current active node.
 */
export const findActiveNavMenuPath = (navMenuRoot: NavMenuItemNode, id?: string): string[] => {
  if (navMenuRoot.childNodes?.length > 0) {
    const foundPath = flatMap(navMenuRoot.childNodes, (childNode) => findActiveNavMenuPath(childNode, id));
    if (foundPath.length > 0) {
      foundPath.unshift(navMenuRoot.id);
      return foundPath;
    }
  }

  if (id ? id === navMenuRoot.id : navMenuRoot.defaultActive) {
    return [navMenuRoot.id];
  }

  return [];
};

/**
 * A utility function that returns the last item in an array, or `undefined` if the array in empty.
 */
export const safelyGetLastArrayItem = <T>(array: T[]): T => (array.length > 0 ? array[array.length - 1] : undefined);

/**
 * A utility function that returns the list of child `id`s of a given parent `id` within a tree of nav
 * `NavMenuItemNode`s.
 *
 * @param rootNode The root node of the tree in which to search.
 * @param ancestorId The `id` of the parent node who's child `id`s are to be returned.
 * @param isUnderAncestor A flag used for internal logic.
 * @returns List of child `id`s
 */
export const findAllDescendentIds = (
  rootNode: NavMenuItemNode,
  ancestorId: string,
  isUnderAncestor: boolean = false
): string[] => {
  const childIds: string[] = [];

  if (rootNode.childNodes?.length > 0) {
    const grandChildIds = flatMap(rootNode.childNodes, (childNode) =>
      findAllDescendentIds(childNode, ancestorId, isUnderAncestor || rootNode.id === ancestorId)
    );
    childIds.push(...grandChildIds);
    if (isUnderAncestor) {
      childIds.unshift(rootNode.id);
    }
  } else if (isUnderAncestor) {
    childIds.push(rootNode.id);
  }

  return childIds;
};

/**
 * A utility function that returns a nav menu item node for a given `id` or `null` if not found.
 *
 * @param rootNode The root node of the tree in which to search.
 * @param id The `id` of the node to find.
 * @returns The node for the given `id` or `null` if not found.
 */
export const getNodeById = (rootNode: NavMenuItemNode, id: string): NavMenuItemNode | null => {
  let foundNode = null;
  if (id) {
    if (rootNode.id === id) {
      foundNode = rootNode;
    } else {
      for (const childNode of rootNode.childNodes ?? []) {
        foundNode = getNodeById(childNode, id);
        if (foundNode) {
          break;
        }
      }
    }
  }
  return foundNode;
};

/**
 * Array.prototype.flatMap polyfill.
 * Source: https://2ality.com/2017/04/flatmap.html#flatmap()
 */
const flatMap = <T, M>(arr: T[], mapFunc: (e: T, i?: number, arr?: T[]) => M | M[]): M[] => {
  const result = [];
  for (let index = 0; index < arr.length; index++) {
    const x = mapFunc(arr[index], index, arr);
    if (Array.isArray(x)) {
      result.push(...x);
    } else {
      result.push(x);
    }
  }
  return result;
};
